import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BarChartIcon from '@mui/icons-material/BarChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

// Chart.js bileşenlerini kaydet
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function CardAnalyticsPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { currentUser, organization } = useAuth();

  // State tanımları
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTags, setSelectedTags] = useState([]);
  const [viewModeByQuestion, setViewModeByQuestion] = useState({});
  const [searchByQuestion, setSearchByQuestion] = useState({});

  const isLikelyImageUrl = (str) => {
    if (typeof str !== 'string') return false;
    return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(str);
  };

  const isLikelyVideoUrl = (str) => {
    if (typeof str !== 'string') return false;
    // Treat as video if it has a common video extension, even before query/hash parameters
    return /\.(mp4|webm|mov|avi|mkv)(?:[?#]|$)/i.test(str);
  };

  const sanitizeFileName = (name) => name.replace(/[^a-z0-9]/gi, '_');

  const getFileExtension = (url, defaultType) => {
    const match = url.match(/\.([0-9a-z]+)(?:[?#]|$)/i);
    if (match && match[1]) return match[1];
    if (defaultType === 'image') return 'jpg';
    if (defaultType === 'video') return 'mp4';
    return 'dat';
  };

  const downloadFile = (url, fileName) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Form ve kanal bilgisini URL'den yükle
  useEffect(() => {
    const formId = searchParams.get('formId');
    if (!formId) {
      setSnackbar({ open: true, message: t('cardReporting.noFormFound'), severity: 'error' });
      setMetaLoading(false);
      return;
    }

    const fetchMeta = async () => {
      try {
        const formSnap = await getDoc(doc(db, 'questionForms', formId));
        if (!formSnap.exists()) {
          setSnackbar({ open: true, message: t('cardReporting.noFormFound'), severity: 'error' });
          setMetaLoading(false);
          return;
        }

        const formData = { id: formSnap.id, ...formSnap.data() };

        if (
          organization?.id &&
          formData.organizationId &&
          formData.organizationId !== organization.id
        ) {
          setSnackbar({ open: true, message: t('cardReporting.noAccess'), severity: 'error' });
          setMetaLoading(false);
          return;
        }

        if (
          organization?.id &&
          !formData.organizationId &&
          formData.createdBy &&
          currentUser?.uid !== formData.createdBy
        ) {
          setSnackbar({ open: true, message: t('cardReporting.noAccess'), severity: 'error' });
          setMetaLoading(false);
          return;
        }

        if (
          !formData.organizationId &&
          organization?.id &&
          formData.createdBy &&
          currentUser?.uid === formData.createdBy
        ) {
          await updateDoc(doc(db, 'questionForms', formData.id), {
            organizationId: organization.id
          });
          formData.organizationId = organization.id;
        }

        setSelectedForm(formData);

        const channelId = formData.channelId || searchParams.get('channelId') || null;
        if (channelId) {
          try {
            const chSnap = await getDoc(doc(db, 'channels', channelId));
            if (chSnap.exists()) {
              const channelData = { id: chSnap.id, ...chSnap.data() };
              setSelectedChannel(channelData);
            } else {
              setSelectedChannel({ id: channelId });
            }
          } catch (err) {
            console.error('Kanal bilgisi alınamadı:', err);
            setSelectedChannel({ id: channelId });
          }
        }
        setSelectedTags([]);
      } catch (err) {
        console.error('Form bilgisi alınamadı:', err);
        setSnackbar({ open: true, message: t('cardReporting.noFormFound'), severity: 'error' });
      } finally {
        setMetaLoading(false);
      }
    };

    fetchMeta();
  }, [searchParams, t, organization, currentUser]);

  // Soruları ve yanıtları çekme
  useEffect(() => {
    if (!selectedForm) return;
    const fetchData = async () => {
      setLoading(true);
      const qQuestions = query(collection(db, 'questions'), where('questionFormId', '==', selectedForm.id));
      const questionsSnap = await getDocs(qQuestions);
      const questionsData = questionsSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        // Keep same order as moderator form builder
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setQuestions(questionsData);

      const constraints = [where('questionFormId', '==', selectedForm.id)];
      const channelId = selectedChannel?.id || selectedForm.channelId;
      if (channelId) {
        constraints.push(where('channelId', '==', channelId));
      }
      const qResponses = query(collection(db, 'responses'), ...constraints);
      const respSnap = await getDocs(qResponses);
      setResponses(respSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchData();
  }, [selectedForm, selectedChannel]);

  // Filtrelenmiş yanıtlar
  const filteredResponses = useMemo(() => {
    const completedOnly = responses.filter((resp) => resp.status === 'completed');
    if (!selectedTags.length) return completedOnly;
    return completedOnly.filter((resp) => {
      const userTags = selectedChannel?.memberDetails?.find((m) => m.userId === resp.userId)?.tags || [];
      return selectedTags.some((tag) => userTags.includes(tag));
    });
  }, [responses, selectedTags, selectedChannel]);

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Grafik ve tablo verilerini hazırla
  const chartData = useMemo(() => {
    if (!questions.length || !filteredResponses.length) return {};

    const dataByQuestion = {};
    questions.forEach((q) => {
      const { questionType, text, options, ratingMin = 0, ratingMax = 5, choices } = q;
      const answers = filteredResponses
        .flatMap((r) => r.finalAnswers || [])
        .filter((a) => a.questionText === text)
        .map((a) => a.answer);

      if (questionType === 'masked') {
        const subtype = q.maskedType || 'number';
        if (!['yesno', 'number', 'date'].includes(subtype)) return;
        if (!answers.length) return;

        if (subtype === 'yesno') {
          const freq = { yes: 0, no: 0 };
          const normalize = (val) => {
            if (typeof val === 'boolean') return val ? 'yes' : 'no';
            const lower = String(val || '').toLowerCase().trim();
            if (['yes', 'evet', 'true', '1'].includes(lower)) return 'yes';
            if (['no', 'hayir', 'hayır', 'false', '0'].includes(lower)) return 'no';
            return null;
          };

          answers.forEach((ans) => {
            const normalized = normalize(ans);
            if (normalized) freq[normalized] = (freq[normalized] || 0) + 1;
          });

          const totalResponses = freq.yes + freq.no;
          if (!totalResponses) return;

          const yesLabel = t('userAnswersPage.yes') || 'Yes';
          const noLabel = t('userAnswersPage.no') || 'No';
          const labels = [yesLabel, noLabel];
          const percentages = [freq.yes, freq.no].map((val) => (val / totalResponses) * 100);

          dataByQuestion[stripHtml(text)] = {
            chart: {
              type: 'bar',
              data: {
                labels,
                datasets: [
                  {
                    label: t('analytics.percentage'),
                    data: percentages,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                  },
                ],
              },
              options: {
                indexAxis: 'y',
                scales: {
                  x: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                  y: { title: { display: true, text: t('analytics.options') } },
                },
                plugins: { title: { display: true, text: stripHtml(text) } },
              },
            },
            tableData: [
              { option: yesLabel, count: freq.yes },
              { option: noLabel, count: freq.no },
            ],
          };
        } else if (subtype === 'number') {
          const freq = {};
          answers.forEach((ans) => {
            const normalized = typeof ans === 'string' ? ans.replace(',', '.') : ans;
            const num = Number(normalized);
            if (Number.isNaN(num)) return;
            const key = num.toString();
            freq[key] = (freq[key] || 0) + 1;
          });

          const totalResponses = Object.values(freq).reduce((sum, v) => sum + v, 0);
          if (!totalResponses) return;

          const labels = Object.keys(freq).sort((a, b) => parseFloat(a) - parseFloat(b));
          const percentages = labels.map((label) => (freq[label] / totalResponses) * 100);

          dataByQuestion[stripHtml(text)] = {
            chart: {
              type: 'bar',
              data: {
                labels,
                datasets: [
                  {
                    label: t('analytics.percentage'),
                    data: percentages,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                  },
                ],
              },
              options: {
                indexAxis: 'y',
                scales: {
                  x: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                  y: { title: { display: true, text: t('analytics.options') } },
                },
                plugins: { title: { display: true, text: stripHtml(text) } },
              },
            },
            tableData: labels.map((label) => ({ option: label, count: freq[label] })),
          };
        } else if (subtype === 'date') {
          const freq = {};
          answers.forEach((ans) => {
            const raw = typeof ans === 'string' ? ans.trim() : String(ans || '').trim();
            if (!raw) return;
            const parsed = new Date(raw);
            const key = Number.isNaN(parsed.getTime()) ? raw : parsed.toISOString().split('T')[0];
            freq[key] = (freq[key] || 0) + 1;
          });

          const totalResponses = Object.values(freq).reduce((sum, v) => sum + v, 0);
          if (!totalResponses) return;

          const labels = Object.keys(freq).sort((a, b) => {
            const ta = Date.parse(a);
            const tb = Date.parse(b);
            if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
            if (!Number.isNaN(ta)) return -1;
            if (!Number.isNaN(tb)) return 1;
            return a.localeCompare(b);
          });
          const percentages = labels.map((label) => (freq[label] / totalResponses) * 100);

          dataByQuestion[stripHtml(text)] = {
            chart: {
              type: 'bar',
              data: {
                labels,
                datasets: [
                  {
                    label: t('analytics.percentage'),
                    data: percentages,
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                  },
                ],
              },
              options: {
                indexAxis: 'y',
                scales: {
                  x: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                  y: { title: { display: true, text: t('analytics.options') } },
                },
                plugins: { title: { display: true, text: stripHtml(text) } },
              },
            },
            tableData: labels.map((label) => ({ option: label, count: freq[label] })),
          };
        }

        return;
      }

      if (!['singleChoice', 'multipleChoice', 'screening', 'pictureChoice', 'ratingQuestion', 'cardSorting'].includes(questionType)) return;

      if (questionType === 'singleChoice' || questionType === 'multipleChoice' || questionType === 'screening' || questionType === 'pictureChoice') {
        const freq = {};
        answers.forEach((ans) => {
          const values = Array.isArray(ans) ? ans : [ans];
          values.forEach((val) => {
            freq[val] = (freq[val] || 0) + 1;
          });
        });
        const totalResponses = answers.length;
        const percentages = Object.fromEntries(
          Object.entries(freq).map(([key, val]) => [key, (val / totalResponses) * 100])
        );

        const choiceLabels = options || choices || Object.keys(freq);

        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels: choiceLabels,
              datasets: [
                {
                  label: t('analytics.percentage'),
                  data: Object.values(percentages),
                  backgroundColor: 'rgba(75, 192, 192, 0.6)',
                },
              ],
            },
            options: {
              indexAxis: 'y',
              scales: {
                x: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                y: { title: { display: true, text: t('analytics.options') } },
              },
              plugins: { title: { display: true, text: stripHtml(text) } },
            },
          },
          tableData: Object.entries(freq).map(([option, count]) => ({ option, count })),
        };
      } else if (questionType === 'ratingQuestion') {
        const freq = {};
        answers.forEach((ans) => {
          const value = parseFloat(ans);
          if (!isNaN(value)) {
            freq[value] = (freq[value] || 0) + 1;
          }
        });
        const totalResponses = answers.length;
        const labels = Array.from({ length: ratingMax - ratingMin + 1 }, (_, i) => i + ratingMin);
        const percentages = labels.map((label) => ((freq[label] || 0) / totalResponses) * 100);

        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels,
              datasets: [
                {
                  label: t('analytics.percentage'),
                  data: percentages,
                  backgroundColor: 'rgba(102, 149, 66, 0.6)',
                },
              ],
            },
            options: {
              scales: {
                y: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                x: { title: { display: true, text: t('analytics.ratingScale') } },
              },
              plugins: { title: { display: true, text: stripHtml(text) } },
            },
          },
          tableData: labels.map((label) => ({ label, count: freq[label] || 0 })),
        };
      } else if (questionType === 'cardSorting') {
        const groupFreq = {};
        answers.forEach((ans) => {
          if (!ans || typeof ans !== 'object' || Array.isArray(ans)) return;
          Object.entries(ans).forEach(([group, cards]) => {
            //if (group === 'unsorted') return;
            if (!groupFreq[group]) groupFreq[group] = {};
            cards.forEach((card, index) => {
              const rank = index + 1; // Determine rank from position
              const cardText = card.text;
              if (!groupFreq[group][cardText]) {
                groupFreq[group][cardText] = { count: 0, sumRank: 0 };
              }
              groupFreq[group][cardText].count += 1;
              groupFreq[group][cardText].sumRank += rank;
            });
          });
        });
      
        // Calculate average rank per card per group
        const cardAverages = [];
        Object.entries(groupFreq).forEach(([group, cards]) => {
          Object.entries(cards).forEach(([card, { sumRank, count }]) => {
            const average = sumRank / count;
            cardAverages.push({
              group,
              card,
              averageRank: average,
              count,
            });
          });
        });
      
        // Sort by group and card for consistent ordering
        cardAverages.sort((a, b) => a.group.localeCompare(b.group) || a.card.localeCompare(b.card));
      
        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels: cardAverages.map(item => `${item.group} - ${item.card}`),
              datasets: [{
                label: t('analytics.averageRank'),
                data: cardAverages.map(item => item.averageRank),
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
              }]
            },
            options: {
              indexAxis: 'y', // Horizontal bars for better readability
              scales: {
                x: { 
                  beginAtZero: true,
                  title: { display: true, text: t('analytics.averageRank') }
                },
                y: { 
                  title: { display: true, text: t('analytics.groupsAndCards') },
                  ticks: {
                    autoSkip: false // Ensure all labels are displayed
                  }
                },
              },
              plugins: { 
                title: { display: true, text: stripHtml(text) },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const value = context.parsed.x;
                      return `${t('analytics.averageRank')}: ${value.toFixed(2)}`;
                    }
                  }
                }
              },
            },
          },
          tableData: cardAverages.map(({ group, card, averageRank, count }) => ({ 
            group, 
            card, 
            averageRank,
            count 
          })),
        };
      }
    });

    return dataByQuestion;
  }, [questions, filteredResponses, t]);

  const summaryByQuestion = useMemo(() => {
    const map = {};

    const extractAnswerParts = (answer) => {
      let textAnswer = '';
      let mediaList = [];
      let videoUrl = '';
      let aiChatTurns = [];

      if (typeof answer === 'object' && answer !== null) {
        if (answer.media && Array.isArray(answer.media)) {
          if (answer.text) textAnswer = answer.text;
          mediaList = answer.media.map((mObj) => {
            const rawType = mObj.fileType || '';
            const normalizedType =
              typeof rawType === 'string' && rawType.toLowerCase().startsWith('video')
                ? 'video'
                : rawType || 'image';

            return {
              type: normalizedType,
              url: mObj.remoteUrl
            };
          });
        } else if (answer.videoUrl) {
          videoUrl = answer.videoUrl;
          mediaList = [{ type: 'video', url: answer.videoUrl }];
        } else if (answer.collageSingleUrl) {
          mediaList = [{ type: 'image', url: answer.collageSingleUrl }];
        } else if (Array.isArray(answer)) {
          if (
            answer.length > 0 &&
            answer.every(
              (msgObj) =>
                msgObj &&
                typeof msgObj === 'object' &&
                'role' in msgObj &&
                'content' in msgObj
            )
          ) {
            aiChatTurns = answer.map((msgObj) => ({
              role: msgObj.role || '',
              content: msgObj.content || ''
            }));
            textAnswer = aiChatTurns
              .map((msgObj) => `[${msgObj.role}] ${msgObj.content}`)
              .join('<br/>');
          } else {
            textAnswer = answer.join(', ');
          }
        } else if (typeof answer.text === 'string') {
          // If the text field itself is just a media URL (e.g., video question), treat it as media
          if (isLikelyVideoUrl(answer.text)) {
            videoUrl = answer.text;
            mediaList = [{ type: 'video', url: answer.text }];
          } else if (isLikelyImageUrl(answer.text)) {
            mediaList = [{ type: 'image', url: answer.text }];
          } else {
            textAnswer = answer.text;
          }
        } else {
          const entries = Object.entries(answer || {});
          if (entries.length) {
            const lines = entries
              .map(([group, cards]) => {
                if (!Array.isArray(cards)) return null;
                const cardTitles = cards.map((c) => c.text || '').filter(Boolean).join(', ');
                return `${group}: ${cardTitles}`;
              })
              .filter(Boolean);
            textAnswer = lines.join(' | ');
          }
        }
      } else if (Array.isArray(answer)) {
        textAnswer = answer.join(', ');
      } else if (typeof answer === 'string') {
        // If the answer itself is a media URL, treat it as media instead of plain text
        if (isLikelyVideoUrl(answer)) {
          mediaList = [{ type: 'video', url: answer }];
        } else if (isLikelyImageUrl(answer)) {
          mediaList = [{ type: 'image', url: answer }];
        } else {
          textAnswer = answer;
        }
      } else if (typeof answer === 'number') {
        textAnswer = String(answer);
      }

      return { textAnswer, mediaList, videoUrl, aiChatTurns };
    };

    filteredResponses.forEach((resp) => {
      const dateStr = resp.completedAt?.toDate ? resp.completedAt.toDate().toLocaleString() : '';
      (resp.finalAnswers || []).forEach((fa) => {
        if (!fa?.questionText) return;
        const questionKey = stripHtml(fa.questionText) || fa.questionText;
        const { textAnswer, mediaList, videoUrl, aiChatTurns } = extractAnswerParts(fa.answer);
        if (!textAnswer && !mediaList.length && !videoUrl && !aiChatTurns.length) return;
        if (!map[questionKey]) map[questionKey] = [];
        map[questionKey].push({
          textAnswer,
          mediaList,
          videoUrl,
          aiChatTurns,
          createdAt: dateStr
        });
      });
    });

    return map;
  }, [filteredResponses]);

  const questionOrder = useMemo(() => {
    const seen = new Set();
    const ordered = [];

    const pushIfNeeded = (key) => {
      if (!key || seen.has(key)) return;
      if (!chartData[key] && !summaryByQuestion[key]) return;
      seen.add(key);
      ordered.push(key);
    };

    questions.forEach((q) => pushIfNeeded(stripHtml(q.text)));
    Object.keys(chartData).forEach((key) => pushIfNeeded(key));
    Object.keys(summaryByQuestion).forEach((key) => pushIfNeeded(key));

    return ordered;
  }, [questions, chartData, summaryByQuestion]);

  const navigationQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedForm?.id) params.set('formId', selectedForm.id);
    const channelId = selectedChannel?.id || selectedForm?.channelId;
    if (channelId) params.set('channelId', channelId);
    if (selectedForm?.workspaceId) params.set('workspaceId', selectedForm.workspaceId);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, [selectedForm, selectedChannel]);

  // Render
  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        background: 'linear-gradient(180deg, #f7f9fc 0%, #eef3fb 100%)'
      }}
    >
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Paper
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 3,
            background:
              'linear-gradient(135deg, rgba(63, 81, 181, 0.06) 0%, rgba(33, 150, 243, 0.06) 100%)',
            border: '1px solid rgba(63, 81, 181, 0.08)',
            boxShadow: '0 10px 30px rgba(63, 81, 181, 0.12)'
          }}
        >

          {/* HEADER ROW: title solda, tab bar sağda */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
            spacing={2}
          >
            
            {/* SOL BLOK: Title, subtitle + published */}
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InsightsIcon color="primary" />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {t('analytics.pageTitle')}
                </Typography>
              </Stack>

              {/* Subtitle + published aynı satırda */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {selectedForm?.name || t('cardReporting.pageTitle')}
                </Typography>

                {selectedForm?.status && (
                  <Chip
                    label={selectedForm.status}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      borderRadius: 1.5,
                      fontWeight: 600,
                      px: 0.6
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {/* TAB BAR */}
            <Stack
              direction={{ xs: 'row', sm: 'row' }}
              spacing={1}
              sx={{
                p: 0.6,
                borderRadius: 999,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                maxWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              <Button
                component={Link}
                to={`/view-answers${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.dashboard')}
              </Button>

              <Button
                component={Link}
                to={`/view-answers-card${navigationQuery}`}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' }
                }}
              >
                {t('formNavigation.reporting')}
              </Button>

              <Button
                component={Link}
                to={`/CardAnalyticsPage${navigationQuery}`}
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: { xs: 1.3, sm: 2 },
                  minWidth: { xs: 'auto', sm: 0 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  boxShadow: 'none'
                }}
              >
                {t('formNavigation.analytics')}
              </Button>
            </Stack>

          </Stack>
        </Paper>

        {metaLoading && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'center',
              borderRadius: 3,
              backgroundColor: '#fff'
            }}
          >
            <CircularProgress />
          </Paper>
        )}

        {!metaLoading && !selectedForm && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: 3 }}>
            {t('cardReporting.noFormFound')}
          </Alert>
        )}

        {!metaLoading && selectedForm && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2.5, sm: 3 },
                backgroundColor: '#fff',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                border: '1px solid #edf1f7'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('analytics.pageTitle') || 'Kart Analiz Sayfası'}
              </Typography>

              {loading ? (
                <Box textAlign="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : questionOrder.length > 0 ? (
                <Stack spacing={3}>
                  {questionOrder.map((questionKey) => {
                    const chartEntry = chartData[questionKey];
                    const chart = chartEntry?.chart;
                    const tableData = chartEntry?.tableData || [];
                    const hasTableData = tableData.length > 0;
                    const hasChart = Boolean(chart);
                    const viewMode = viewModeByQuestion[questionKey] || (hasChart ? 'chart' : 'table');
                    const handleViewChange = (_, next) => {
                      if (!next) return;
                      setViewModeByQuestion((prev) => ({ ...prev, [questionKey]: next }));
                    };

                    const textAnswers = summaryByQuestion[questionKey] || [];
                    const searchValue = searchByQuestion[questionKey] || '';
                    const filteredTextAnswers = textAnswers.filter((item) => {
                      const aiText = Array.isArray(item.aiChatTurns)
                        ? item.aiChatTurns.map((turn) => `${turn.role} ${turn.content}`).join(' ')
                        : '';
                      const haystack = `${item.textAnswer || ''} ${item.mediaList
                        .map((m) => m.url)
                        .join(' ')} ${item.videoUrl || ''} ${aiText}`.toLowerCase();
                      return haystack.includes(searchValue.toLowerCase());
                    });

                    const handleCardDownload = (answer, index) => {
                      const media = answer.mediaList?.[0] || (answer.videoUrl ? { type: 'video', url: answer.videoUrl } : null);
                      if (!media?.url) return;
                      const fileName = `${sanitizeFileName(selectedForm?.name || 'Form')}-${sanitizeFileName(stripHtml(questionKey))}-${index + 1}.${getFileExtension(media.url, media.type)}`;
                      downloadFile(media.url, fileName);
                    };

                    const questionLabel =
                      stripHtml(
                        questions.find((q) => stripHtml(q.text) === questionKey)?.text || questionKey
                      ) || questionKey;

                    return (
                      <Box
                        key={questionKey}
                        sx={{
                          p: { xs: 2, sm: 2.5 },
                          borderRadius: 2,
                          border: '1px solid #edf1f7',
                          backgroundColor: '#fafbff',
                          boxShadow: '0 6px 18px rgba(0,0,0,0.04)'
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1.5 }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {questionLabel}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {hasChart && (
                              <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={viewMode}
                                onChange={handleViewChange}
                                color="primary"
                              >
                                <ToggleButton value="table">
                                  <TableRowsIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="chart">
                                  <BarChartIcon fontSize="small" />
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                          </Stack>
                        </Stack>

                        {hasChart && viewMode === 'chart' ? (
                          <Box
                            sx={{
                              height: 400,            // 🔹 grafiğin kaplayacağı maksimum yükseklik
                              overflowY: 'auto',         // 🔹 içerik taşarsa kendi içinde scroll
                              mt: 1
                            }}
                          >
                            <Bar
                              data={chart.data}
                              options={{
                                ...chart.options,
                                maintainAspectRatio: false // 🔹 kutu yüksekliğine uyum sağlasın
                              }}
                            />
                          </Box>
                        ) : (
                          hasTableData && (
                            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                              {t('analytics.tableTitle', { question: questionLabel })}
                            </Typography>
                          )
                        )}

                        {viewMode === 'table' && hasTableData && (
                          <>
                            {tableData[0]?.option ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('analytics.option')}</TableCell>
                                    <TableCell>{t('analytics.count')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {tableData.map((data, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{data.option}</TableCell>
                                      <TableCell>{data.count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : tableData[0]?.label ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('analytics.rating')}</TableCell>
                                    <TableCell>{t('analytics.count')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {tableData.map((data, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{data.label}</TableCell>
                                      <TableCell>{data.count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>{t('analytics.group')}</TableCell>
                                    <TableCell>{t('analytics.card')}</TableCell>
                                    <TableCell>{t('analytics.averageRank')}</TableCell>
                                    <TableCell>{t('analytics.count')}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {tableData.map((data, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{data.group}</TableCell>
                                      <TableCell>{data.card}</TableCell>
                                      <TableCell>{data.averageRank.toFixed(2)}</TableCell>
                                      <TableCell>{data.count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </>
                        )}

                        {!hasChart && filteredTextAnswers.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {t('analytics.textAnswers') }
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={t('analytics.searchResponses') || 'Yanıtları ara'}
                              value={searchValue}
                              onChange={(e) =>
                                setSearchByQuestion((prev) => ({ ...prev, [questionKey]: e.target.value }))
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                )
                              }}
                              sx={{ mb: 1.5 }}
                            />
                            <Grid container spacing={1.5}>
                              {filteredTextAnswers.map((answer, idx) => {
                                const mediaPreview = answer.mediaList?.[0];
                                const hasMedia = Boolean(mediaPreview?.url || answer.videoUrl);
                                const aiChatTurns = Array.isArray(answer.aiChatTurns)
                                  ? answer.aiChatTurns
                                  : [];
                                const hasAiChat = aiChatTurns.length > 0;
                                const cardClickable = hasMedia;
                                const onClick = () => {
                                  if (!cardClickable) return;
                                  handleCardDownload(answer, idx);
                                };

                                return (
                                  <Grid item xs={12} sm={6} md={4} key={`${questionKey}-${idx}`}>
                                    <Card
                                      variant="outlined"
                                      onClick={onClick}
                                      sx={{
                                        borderRadius: 2,
                                        maxHeight: 200,              // 🔹 kartın maksimum yüksekliği
                                        cursor: cardClickable ? 'pointer' : 'default',
                                        overflow: 'hidden',          // içerden taşanı gizle
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.15s ease',
                                        '&:hover': {
                                          transform: cardClickable ? 'translateY(-2px)' : 'none',
                                          boxShadow: cardClickable ? '0 12px 24px rgba(0,0,0,0.08)' : 'none'
                                        }
                                      }}
                                    >
                                      <CardContent 
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: 1,
                                              flex: 1,
                                              overflowY: 'auto'          // 🔹 içerik içinde dikey scroll
                                            }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            {t('analytics.answer') }
                                          </Typography>
                                          {cardClickable && <CloudDownloadIcon fontSize="small" color="primary" />}
                                        </Box>
                                        {hasAiChat && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-start',
                                              gap: 0.5
                                            }}
                                          >
                                            {aiChatTurns.map((turn, turnIdx) => {
                                              const role = (turn.role || '').toLowerCase();
                                              const content = turn.content || '';
                                              let icon = null;
                                              let bgColor = '#f3f4ff';
                                              let title = '';

                                              if (role === 'assistant') {
                                                icon = <SmartToyIcon sx={{ fontSize: 14 }} />;
                                                bgColor = '#e0f2fe';
                                                title = 'AI';
                                              } else if (role === 'user') {
                                                icon = <PersonIcon sx={{ fontSize: 14 }} />;
                                                bgColor = '#e5e7eb';
                                                title = 'User';
                                              } else if (role === 'question' || role === 'system') {
                                                icon = <HelpOutlineIcon sx={{ fontSize: 14 }} />;
                                                bgColor = '#ede9fe';
                                                title = 'Question';
                                              }

                                              return (
                                                <Box
                                                  key={turnIdx}
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    px: 1.2,
                                                    py: 0.35,
                                                    borderRadius: 999,
                                                    backgroundColor: bgColor,
                                                    fontSize: 13,
                                                    maxWidth: '100%',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    gap: 0.5
                                                  }}
                                                  title={title}
                                                >
                                                  {icon}
                                                  <span>{content}</span>
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                        )}
                                        {answer.textAnswer && !hasAiChat && (
                                          <Box
                                            sx={{ fontSize: 14, lineHeight: 1.4 }}
                                            dangerouslySetInnerHTML={{ __html: answer.textAnswer }}
                                          />
                                        )}
                                        {mediaPreview?.url &&
                                          (mediaPreview.type === 'video' || isLikelyVideoUrl(mediaPreview.url) ? (
                                            <video
                                              width="100%"
                                              height="auto"
                                              controls
                                              style={{ borderRadius: 8 }}
                                            >
                                              <source
                                                src={mediaPreview.url}
                                                type={`video/${getFileExtension(mediaPreview.url, 'video')}`}
                                              />
                                              {t('analytics.unsupportedMedia')}
                                            </video>
                                          ) : (
                                            <CardMedia
                                              component="img"
                                              image={mediaPreview.url}
                                              alt="preview"
                                              sx={{ borderRadius: 1, maxHeight: 160, objectFit: 'cover' }}
                                            />
                                          ))}
                                        {answer.videoUrl && (
                                          <video
                                            width="100%"
                                            height="auto"
                                            controls
                                            style={{ borderRadius: 8 }}
                                          >
                                            <source
                                              src={answer.videoUrl}
                                              type={`video/${getFileExtension(answer.videoUrl, 'video')}`}
                                            />
                                            {t('analytics.unsupportedMedia')}
                                          </video>
                                        )}
                                        {answer.createdAt && (
                                          <Typography variant="caption" color="text.secondary">
                                            {answer.createdAt}
                                          </Typography>
                                        )}
                                        {hasMedia && (
                                          <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<CloudDownloadIcon fontSize="small" />}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCardDownload(answer, idx);
                                            }}
                                            sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
                                          >
                                            {t('analytics.download')}
                                          </Button>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography>{t('analytics.noData') }</Typography>
              )}
            </Paper>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
